/**
 * The reference-letter template: turns the PI's answers to the letter form into a letter (markdown),
 * which the PI then edits freely. Pure, so it can be tested and regenerated.
 *
 *   buildLetter(form) -> markdown
 *   emptyForm(prefill) -> form with what Otable already knows (GET /consortium/labs/<id>/letters/prefill/)
 *
 * The candidate's pronouns come from the form (default "they"); the letter never guesses them from a name.
 */

export const PRONOUNS = {
  they: { label: 'they/them', subj: 'they', obj: 'them', poss: 'their', is: 'are', was: 'were', has: 'have', s: false },
  she: { label: 'she/her', subj: 'she', obj: 'her', poss: 'her', is: 'is', was: 'was', has: 'has', s: true },
  he: { label: 'he/him', subj: 'he', obj: 'him', poss: 'his', is: 'is', was: 'was', has: 'has', s: true },
};

export const STRENGTHS = [
  ['independent', 'Independent', 'an independent thinker who takes ownership of a question'],
  ['rigorous', 'Rigorous', 'rigorous and careful in designing and carrying out experiments'],
  ['creative', 'Creative', 'creative in finding new ways around a problem'],
  ['collaborative', 'Collaborative', 'a generous and effective collaborator'],
  ['communicator', 'Clear communicator', 'a clear communicator, in writing as well as in presentations'],
  ['reliable', 'Reliable', 'reliable and well organised'],
  ['learner', 'Fast learner', 'a fast learner who picks up new methods quickly'],
  ['leadership', 'Leadership', 'able to lead a project and the people working on it'],
  ['mentoring', 'Mentoring', 'a patient mentor to the younger members of the lab'],
  ['resilient', 'Resilient', 'resilient and constructive when experiments do not go to plan'],
];

export const RANKINGS = [
  ['', 'No comparison'],
  ['1', 'Top 1%'],
  ['5', 'Top 5%'],
  ['10', 'Top 10%'],
  ['25', 'Top 25%'],
];

export const LEVELS = [
  ['highest', 'Highest recommendation, without reservation'],
  ['strong', 'Strong recommendation'],
  ['standard', 'Recommendation'],
];

export const RELATIONSHIPS = [
  ['supervisor', 'Their PI and direct supervisor'],
  ['head', 'Head of the lab they worked in'],
  ['cosupervisor', 'Their co-supervisor'],
];

const lines = (text) => (text || '').split('\n').map((l) => l.trim().replace(/^[-•*]\s*/, '')).filter(Boolean);
const join = (items) => (items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);
const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);
const article = (word) => (/^[aeiou]/i.test(word || '') ? 'an' : 'a');

export function emptyForm(pre = {}) {
  const c = pre.candidate || {};
  const a = pre.author || {};
  return {
    title: '',
    recipient: '',
    purpose: '',
    name: c.name || '',
    first_name: c.first_name || '',
    pronouns: 'they',
    role: c.role || '',
    start_year: c.start_year || '',
    end_year: c.end_year || '',
    is_alumni: !!c.is_alumni,
    current_position: c.current_position || '',
    relationship: 'supervisor',
    research: '',
    skills: c.skills || [],
    strengths: [],
    achievements: '',
    ranking: '',
    level: 'strong',
    personal: '',
    lab_name: pre.lab?.name || '',
    author_name: a.name || '',
    author_title: a.title || '',
    author_institution: a.institution || pre.lab?.institution || '',
    author_email: a.email || '',
  };
}

export function buildLetter(f, today = new Date()) {
  const p = PRONOUNS[f.pronouns] || PRONOUNS.they;
  const name = (f.name || '').trim() || 'the candidate';
  const first = (f.first_name || '').trim() || name;
  const past = !!f.is_alumni;
  const purpose = (f.purpose || '').trim();
  const lab = (f.lab_name || '').trim() ? `the ${f.lab_name.trim()}` : 'my lab';
  // a name takes the singular verb whatever the pronouns ("Karen is"; "they are")
  const nameIs = past ? 'was' : 'is';
  const role = (f.role || '').trim();
  const out = [];

  out.push(today.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }));
  out.push(`Dear ${(f.recipient || '').trim() || 'Selection Committee'},`);

  // why I am writing, and how I know the candidate
  const rel = {
    supervisor: 'as their PI and direct supervisor',
    head: 'as the head of the lab in which they worked',
    cosupervisor: 'as their co-supervisor',
  }[f.relationship] || 'as their PI';
  const relP = rel.replace(/\btheir\b/g, p.poss).replace(/\bthey\b/g, p.subj);
  const intro = `It is my pleasure to recommend ${name}${purpose ? ` for ${purpose}` : ''}.`;
  let period;
  if (past) {
    const span = f.start_year && f.end_year ? ` from ${f.start_year} to ${f.end_year}` : f.start_year ? ` from ${f.start_year}` : '';
    period = `I know ${first} ${relP}: ${p.subj} ${p.was} a member of ${lab}${span}${role ? ` as ${article(role)} ${role}` : ''}`
      + `${f.current_position ? `, and ${p.subj} ${p.is} now ${f.current_position}` : ''}.`;
  } else {
    period = `I know ${first} ${relP}: ${p.subj} ${p.has} been a member of ${lab}${f.start_year ? ` since ${f.start_year}` : ''}`
      + `${role ? `, working as ${article(role)} ${role}` : ''}.`;
  }
  out.push(`${intro} ${period}`);

  // the work
  const research = (f.research || '').trim();
  const achievements = lines(f.achievements);
  if (research || achievements.length) {
    let para = '';
    if (research) para += `During ${p.poss} time in ${lab}, ${first} ${past ? 'worked' : 'has been working'} on ${research.replace(/\.$/, '')}.`;
    if (achievements.length === 1) para += `${para ? ' ' : ''}Among ${p.poss} achievements, I would highlight ${achievements[0].replace(/\.$/, '')}.`;
    out.push(para.trim());
    if (achievements.length > 1) {
      out.push(`Among ${p.poss} achievements, I would highlight:`);
      out.push(achievements.map((a) => `- ${a.replace(/\.$/, '')}`).join('\n'));
    }
  }

  // skills and qualities
  const skills = (f.skills || []).filter(Boolean);
  if (skills.length) {
    out.push(`${cap(p.subj)} ${p.has} built a solid command of ${join(skills)}, and I have seen ${p.obj} apply these skills with growing independence.`);
  }
  const qualities = (f.strengths || []).map((k) => STRENGTHS.find(([key]) => key === k)?.[2]).filter(Boolean);
  if (qualities.length) {
    out.push(`Beyond ${p.poss} technical ability, ${first} ${nameIs} ${join(qualities)}.`);
  }

  // comparison and recommendation
  const closing = [];
  if (f.ranking) closing.push(`Among the students and researchers I have supervised, I would place ${first} in the top ${f.ranking}%.`);
  const goal = purpose ? ` for ${purpose}` : '';
  closing.push({
    highest: `I recommend ${first}${goal} with the highest enthusiasm and without reservation.`,
    strong: `I strongly recommend ${first}${goal}.`,
    standard: `I recommend ${first}${goal}.`,
  }[f.level] || `I recommend ${first}${goal}.`);
  if ((f.personal || '').trim()) closing.unshift(f.personal.trim().replace(/([^.!?])$/, '$1.'));
  out.push(closing.join(' '));

  out.push(`Please do not hesitate to contact me${f.author_email ? ` at ${f.author_email}` : ''} should you need any further information.`);
  out.push('Sincerely,');
  const sign = [f.author_name, f.author_title, [f.lab_name, f.author_institution].filter(Boolean).join(', '), f.author_email]
    .map((x) => (x || '').trim()).filter(Boolean);
  out.push(...sign);
  return out.join('\n\n');
}
